import type { OperationManifest, ConnectorInfo } from '@/types/operationManifest';
import { OperationOptions, SettingScope } from '@/types/operationManifest';

// Native agent badge: Fluent `BotFilled` on the blue `agentNativeBrand`
// tile (`ottoTokens.agentNativeBrand` = `#2563EB`). The picker icon, the
// canvas badge, and `AgentVariantIcon('native')` all read as the same
// variant. Path is the exact `BotFilled` outline from `@fluentui/react-icons`.
const NATIVE_AGENT_BRAND = '#2563EB';
const NATIVE_AGENT_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none"><rect width="22" height="22" rx="4" fill="#2563EB"/><g transform="translate(1,1)" fill="#FFFFFF"><path d="M14.7 11.5c.99 0 1.8.81 1.8 1.81v.7a3.52 3.52 0 0 1-1.13 2.55c-.95.87-2.6 1.44-5.37 1.44s-4.42-.57-5.37-1.44A3.52 3.52 0 0 1 3.5 14H3.5v-.69c0-1 .81-1.8 1.8-1.8h9.4ZM6.5 3C5.67 3 5 3.67 5 4.5v4c0 .83.67 1.5 1.5 1.5h7c.83 0 1.5-.67 1.5-1.5v-4c0-.83-.67-1.5-1.5-1.5h-3v-.5c0-.3-.22-.5-.5-.5a.5.5 0 0 0-.5.5V3h-3ZM7 6.5a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm4 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"/></g></svg>';
const NATIVE_AGENT_ICON_URI = `data:image/svg+xml;utf8,${encodeURIComponent(NATIVE_AGENT_SVG)}`;

export default {
  properties: {
    iconUri: NATIVE_AGENT_ICON_URI,
    brandColor: NATIVE_AGENT_BRAND,
    description:
      'Loop in which the AI agent decides at each step which tools to use and how, and which text to generate to respond to the user.',

    allowChildOperations: true,
    subGraphDetails: {
      tools: {
        isAdditive: true,
        location: ['actions'],
        inputs: {
          type: 'object',
          properties: {
            description: {
              type: 'string',
              title: 'Description',
              description: 'Enter some explanation of when you would use this tool.',
            },
            agentParameterSchema: {
              title: 'Agent Parameters',
              description: 'Initialize Agent Parameters',
              type: 'object',
              'x-ms-visibility': 'important',
              'x-ms-editor': 'initializevariable',
              'x-ms-editor-options': {
                isAgentParameter: true,
              },
            },
          },
          required: ['description'],
        },
        inputsLocation: [],
        allowOperations: true,
      },
    },
    inputs: {
      type: 'object',
      properties: {
        agentModelType: {
          title: 'Agent model source',
          description: 'Source where your agent model is hosted.',
          'x-ms-editor': 'dropdown',
          'x-ms-visibility': 'hideInUI',
          'x-ms-editor-options': {
            readOnly: true,
            options: [
              {
                value: 'AzureOpenAI',
                displayName: 'Azure OpenAI',
              },
              {
                value: 'FoundryAgentService',
                displayName: 'Foundry Agent Service (Preview)',
                unSupportedWorkflowKind: ['agent'],
              },
              {
                value: 'FoundryAgentServiceV2',
                displayName: 'Foundry Agent Service V2 (Preview)',
                unSupportedWorkflowKind: ['agent'],
              },
              {
                value: 'APIMGenAIGateway',
                displayName: 'APIM Gen AI Gateway (Preview)',
              },
              {
                value: 'V1ChatCompletionsService',
                displayName: 'V1 Chat Completions Service (Preview)',
              },
            ],
          },
          type: 'string',
          default: 'AzureOpenAI',
        },
        deploymentId: {
          type: 'string',
          title: 'AI model',
          description: 'The deployment name for the model to use.',
          'x-ms-create-new-resource': true,
          'x-ms-visibility': 'important',
          'x-ms-editor': 'combobox',
          'x-ms-connection-required': true,
          'x-ms-connection-options': {
            'x-ms-visibility': 'subLabelOnly',
          },
          'x-ms-input-dependencies': {
            type: 'visibility',
            parameters: [
              {
                name: 'agentModelType',
                values: ['AzureOpenAI', 'FoundryAgentService', 'FoundryAgentServiceV2', 'APIMGenAIGateway', 'GitHubCopilot'],
              },
            ],
          },
        },
        modelId: {
          type: 'string',
          title: 'Model Id',
          description: 'The model identifier to use (e.g., claude-3-5-sonnet, gpt-4, etc.).',
          'x-ms-visibility': 'important',
          'x-ms-input-dependencies': {
            type: 'visibility',
            parameters: [
              {
                name: 'agentModelType',
                values: ['V1ChatCompletionsService'],
              },
            ],
          },
        },
        _systemMessage: {
          type: 'string',
          title: 'System message',
          description: 'Instructions that define the agent\'s behavior, capabilities, and constraints.',
          'x-ms-visibility': 'important',
          'x-ms-editor': 'textarea',
        },
        _userMessage: {
          type: 'string',
          title: 'User message',
          description: 'The initial user prompt or question for the agent to respond to.',
          'x-ms-visibility': 'important',
          'x-ms-editor': 'textarea',
        },
        headers: {
          type: 'object',
          title: 'Headers',
          description: 'Custom headers to include in the request',
          'x-ms-editor': 'dictionary',
          'x-ms-visibility': 'important',
          'x-ms-input-dependencies': {
            type: 'visibility',
            parameters: [
              {
                name: 'agentModelType',
                values: ['V1ChatCompletionsService'],
              },
            ],
          },
        },
        agentModelSettings: {
          type: 'object',
          'x-ms-visibility': 'advanced',
          properties: {
            agentChatCompletionSettings: {
              type: 'object',
              properties: {
                maxTokens: {
                  title: 'Max tokens',
                  description: 'Max tokens to use (value should be between 1 and 8192)',
                  type: 'integer',
                  format: 'int32',
                  minimum: 1,
                  maximum: 8192,
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelType',
                        values: ['AzureOpenAI'],
                      },
                    ],
                  },
                },
                frequencyPenalty: {
                  title: 'Frequency penalty',
                  description: 'Penalty for frequency of tokens (value should be between -2 and 2)',
                  type: 'number',
                  format: 'float',
                  minimum: -2.0,
                  maximum: 2.0,
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelType',
                        values: ['AzureOpenAI'],
                      },
                    ],
                  },
                },
                presencePenalty: {
                  title: 'Presence penalty',
                  description: 'Penalty for presence of tokens (value should be between -2 and 2)',
                  type: 'number',
                  minimum: -2.0,
                  format: 'float',
                  maximum: 2.0,
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelType',
                        values: ['AzureOpenAI'],
                      },
                    ],
                  },
                },
                temperature: {
                  title: 'Temperature',
                  description: 'Sampling temperature to use (value should be between 0 and 2)',
                  type: 'number',
                  format: 'float',
                  minimum: 0,
                  maximum: 2.0,
                },
                topP: {
                  title: 'Top P',
                  description: 'Nucleus sampling parameter (value should be between 0 and 1)',
                  type: 'number',
                  format: 'float',
                  minimum: 0,
                  maximum: 1.0,
                },
                builtinTools: {
                  type: 'array',
                  title: 'Built in tools',
                  'x-ms-editor': 'dropdown',
                  'x-ms-editor-options': {
                    multiSelect: true,
                    titleSeparator: ',',
                    serialization: {
                      valueType: 'array',
                    },
                    options: [
                      {
                        value: 'code_interpreter',
                        displayName: 'Code interpreter',
                      },
                    ],
                  },
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelType',
                        values: ['FoundryAgentService', 'FoundryAgentServiceV2'],
                      },
                    ],
                  },
                },
                metadata: {
                  type: 'object',
                  title: 'Metadata',
                  description: 'Additional metadata to pass to the model',
                  'x-ms-editor': 'dictionary',
                  'x-ms-editor-options': {
                    valueType: 'string',
                  },
                  'x-ms-visibility': 'important',
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelType',
                        values: ['V1ChatCompletionsService'],
                      },
                    ],
                  },
                },
              },
            },
            agentHistoryReductionSettings: {
              type: 'object',
              properties: {
                agentHistoryReductionType: {
                  type: 'string',
                  title: 'Agent history reduction type (experimental)',
                  default: 'maximumTokenCountReduction',
                  description: 'Type of agent history reduction to use',
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelType',
                        values: ['AzureOpenAI'],
                      },
                    ],
                  },
                  'x-ms-editor': 'dropdown',
                  'x-ms-editor-options': {
                    options: [
                      {
                        value: 'messageCountReduction',
                        displayName: 'Message count reduction',
                      },
                      {
                        value: 'maximumTokenCountReduction',
                        displayName: 'Token count reduction',
                      },
                    ],
                  },
                },
                messageCountLimit: {
                  type: 'integer',
                  title: 'Message token count',
                  description: 'The maximum number of messages to keep in the agent history',
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelSettings.agentHistoryReductionSettings.agentHistoryReductionType',
                        values: ['messageCountReduction'],
                      },
                    ],
                  },
                },
                maximumTokenCount: {
                  type: 'integer',
                  title: 'Maximum token count',
                  description: 'The maximum number of tokens to use for the agent history',
                  default: 128000,
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelSettings.agentHistoryReductionSettings.agentHistoryReductionType',
                        values: ['maximumTokenCountReduction'],
                      },
                    ],
                  },
                },
              },
            },
            deploymentModelProperties: {
              type: 'object',
              properties: {
                name: {
                  title: 'Model name',
                  description: 'Name of the model which you want to use for the agent',
                  type: 'string',
                  default: 'gpt-4.1',
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelType',
                        values: ['AzureOpenAI'],
                      },
                    ],
                  },
                },
                format: {
                  title: 'Model format',
                  description: 'Format of the model you are using',
                  type: 'string',
                  default: 'OpenAI',
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelType',
                        values: ['AzureOpenAI'],
                      },
                    ],
                  },
                },
                version: {
                  title: 'Model version',
                  description: 'Version of the model you are using',
                  type: 'string',
                  default: '2025-04-14',
                  'x-ms-input-dependencies': {
                    type: 'visibility',
                    parameters: [
                      {
                        name: 'agentModelType',
                        values: ['AzureOpenAI'],
                      },
                    ],
                  },
                },
              },
            },
          },
        },
        knowledgeBases: {
          type: 'array',
          title: 'Knowledge Bases',
          description: 'Knowledge sources the agent can query for context during reasoning.',
          'x-ms-visibility': 'advanced',
          items: {
            type: 'object',
            properties: {
              knowledgeHubName: {
                type: 'string',
                title: 'Knowledge Hub Name',
              },
              sourceType: {
                type: 'string',
                title: 'Source Type',
                enum: ['DocumentUpload', 'FoundryIQ', 'AzureAISearch', 'WorkIQ'],
              },
              retrievalSettings: {
                type: 'object',
                title: 'Retrieval Settings',
                properties: {
                  topK: { type: 'integer', title: 'Top K' },
                  scoreThreshold: { type: 'number', title: 'Score Threshold' },
                  retrievalReasoningEffort: {
                    type: 'string',
                    title: 'Reasoning Effort',
                    enum: ['minimal', 'low', 'medium'],
                  },
                },
              },
            },
            required: ['knowledgeHubName', 'sourceType'],
          },
        },
      },
      // `deploymentId` and `modelId` are mutually exclusive based on `agentModelType`
      // (see `x-ms-input-dependencies` above). Hidden parameters are filtered out
      // before validation, so only the currently-visible model field is enforced.
      required: ['agentModelType', 'deploymentId', 'modelId', '_systemMessage'],
    },
    outputs: {
      type: 'object',
      properties: {
        lastAssistantMessage: {
          type: 'object',
          title: 'Last Assistant Message',
          description: 'This is the final message returned by the model',
        },
      },
    },
    isOutputsOptional: false,
    channels: {
      type: 'object',
      properties: {
        in: {
          type: 'object',
        },
        out: {},
      },
    },
    inputsLocation: ['inputs', 'parameters'],
    isInputsOptional: false,

    supportedChannels: [
      {
        input: {
          type: 'Request',
        },
        output: {
          type: 'Response',
        },
      },
    ],

    connection: {
      required: true,
      type: 'agent',
    },

    connectionReference: {
      referenceKeyFormat: 'agentconnection',
    },

    connector: {
      id: '/connectionProviders/agent',
      name: 'Agent',
      properties: {
        description: 'Agent operations',
        displayName: 'Agent',
      },
      isSecureByDefault: true,
    } as ConnectorInfo,

    settings: {
      trackedProperties: {
        scopes: [SettingScope.Action],
      },
      timeout: {
        scopes: [SettingScope.Action],
      },
      count: {
        scopes: [SettingScope.Action],
      },
      retryPolicy: {
        scopes: [SettingScope.Action],
      },
      operationOptions: {
        scopes: [SettingScope.Action],
        options: [OperationOptions.FailWhenLimitsReached],
      },
    },
  },
} as OperationManifest;
